import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep } from "lodash";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router";
import {
  updateActiveFormInstance,
  setActiveFormInstance,
  usePermissionCheck,
} from "../../store/base";
import usePermissionRedirect from "../../hooks/usePermissionRedirect";
import {
  useRoundingErrand,
  constants,
  overviewUrl,
  executeUrl,
} from "../../store/roundingErrands";
import { getErrandStatus } from "../../components/utils/errandUtils";

//components & styles
import ErrandOverview from "./detail/Overview";
import ModalForm from "../../components/Forms/RoundingErrand/ChapterForm/ModalForm";
import DelegateModalForm from "../../components/Forms/RoundingErrand/DelegateErrandChapterForm/ModalForm";
import DeleteModal from "../../components/Forms/Delete/DeleteModal";
import DetailPageHeaderMenu from "../Layouts/DetailPageHeaderMenu/DetailPageHeaderMenu";
import { DetailLayoutWrapper } from "../../components/sharedStyles";
import { ErrandStatusLabel } from "../../components/Lists/Base/CompleteList/styles";
import Errand from "./detail/Errand";

function RoundingErrandDetail() {
  const dispatch = useDispatch();
  const storeName = constants.STORE_NAME;
  const { roundingErrandId } = useParams();
  const { url, path } = useRouteMatch();
  const { replace, push } = useHistory();

  const { id, user_type } = useSelector((state) => state.app?.user);

  const [roundingErrand, roundingErrandLoading] =
    useRoundingErrand(roundingErrandId);

  const [editErrandOpen, setEditErrandOpen] = React.useState(false);
  const [delegateErrandOpen, setDelegateErrandOpen] = React.useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  usePermissionRedirect(["view_can_roundingerrand", "allow_errand_roundings"]);

  const canEdit = usePermissionCheck("change_can_roundingerrand");
  const canDelete = usePermissionCheck("delete_can_roundingerrand");

  const SUB_PAGES = [
    {
      path: "/overview",
      label: "Ärende",
      component: ErrandOverview,
    },
  ];

  if (roundingErrand?.status === 3 || roundingErrand?.status === 4) {
    SUB_PAGES.push({
      path: "/report",
      label: "Utfall",
      component: Errand,
    });
  }

  const subPageLinks = SUB_PAGES.map((sp) => ({
    url: `${url}${sp.path}`,
    label: sp.label,
  }));

  const onEdit = () => {
    setEditErrandOpen(true);
  };

  const onExecute = () => {
    push(executeUrl({ id: roundingErrandId }));
  };

  const canUserExecuteRoundingErrand = () => {
    let currentPerformer = false;
    if (roundingErrand?.performer) {
      currentPerformer = roundingErrand?.performer?.user?.id;
    }

    //Errand already done
    if (roundingErrand?.status === 3 || roundingErrand?.status === 4) {
      return false;
    }

    if (!canEdit) {
      return false;
    }
    if (user_type === 0 || user_type === 1) {
      return canEdit;
    }
    if (currentPerformer === id) {
      return canEdit;
    }
  };

  const canDelegateErrand = () => {
    //errand already done
    if (roundingErrand?.status === 3 || roundingErrand?.status === 4) {
      return false;
    }

    //is admin or standarduser with correct permissions
    if (user_type === 0 || user_type === 1) {
      return canEdit;
    }
  };

  const onDelegate = () => {
    const errandClone = cloneDeep(roundingErrand);
    dispatch(updateActiveFormInstance({ storeName, data: errandClone }));
    setDelegateErrandOpen(true);
  };

  const handleActions = [];

  if (canEdit) {
    handleActions.push({
      name: "Utför ärende",
      onClick: onExecute,
      hidden: !canUserExecuteRoundingErrand(),
    });
  }

  if (canEdit) {
    handleActions.push(
      {
        name: "Delegera ärende",
        onClick: onDelegate,
        hidden: !canDelegateErrand(),
      },
      {
        name: "Redigera",
        onClick: onEdit,
      }
    );
  }

  if (canDelete) {
    handleActions.push({
      name: "Radera",
      onClick: () => setDeleteModalOpen(true),
      isDelete: true,
    });
  }

  const { status, display } = getErrandStatus({
    status: roundingErrand?.status,
    plannedStart: roundingErrand?.planned_start,
    plannedEnd: roundingErrand?.planned_end,
    actualStart: roundingErrand?.execute_start,
    actualEnd: roundingErrand?.execute_end,
  });

  const renderInfoPills = () => {
    return (
      <>
        <ErrandStatusLabel state={status}>{display}</ErrandStatusLabel>
      </>
    );
  };

  React.useEffect(() => {
    return () => {
      dispatch(setActiveFormInstance({ storeName, data: {} }));
    };
  }, []);

  return (
    <>
      <DeleteModal
        isOpen={deleteModalOpen}
        closeFunction={() => setDeleteModalOpen(false)}
        instance={roundingErrand}
        constants={constants}
        deletedCallback={() => replace(overviewUrl({ isGov: false }))}
      />

      {canEdit && editErrandOpen && (
        <ModalForm
          method="PATCH"
          id={roundingErrand?.id}
          instance={roundingErrand}
          onCheckout={() => setEditErrandOpen(false)}
        />
      )}

      {delegateErrandOpen && (
        <DelegateModalForm
          method="PATCH"
          id={roundingErrand?.id}
          isOpen={delegateErrandOpen}
          onCheckout={() => setDelegateErrandOpen(false)}
        />
      )}

      <DetailLayoutWrapper>
        <DetailPageHeaderMenu
          title={`Ärende ${roundingErrand?.str_representation || "laddar..."}`}
          breadCrumbs={[
            { url: "/rounding-errands/errands", label: "Ronderingar" },
            { label: roundingErrand?.str_representation || "Laddar..." },
          ]}
          {...{ subPages: subPageLinks, handleActions, renderInfoPills }}
        />

        <Switch>
          {SUB_PAGES.map((sp) => (
            <Route
              key={sp.path}
              path={`${path}${sp.path}`}
              component={sp.component}
            />
          ))}

          {/* Default sub page  */}
          <Redirect exact to={`${url}${SUB_PAGES[0].path}`} path={`${path}`} />
        </Switch>
      </DetailLayoutWrapper>
    </>
  );
}

export default RoundingErrandDetail;
